import { useMutation } from '@tanstack/react-query'

import type { PlaceBidResponse } from '@b-stock/auction-api-client'
import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { getBidsClient } from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'

export type BuyNowParams =
  | { auctionId: string; amount?: never; buyerGroupId?: never }
  | {
      auctionId: string
      /** First Dibs: the group's early-access price. */
      amount: number
      /** First Dibs buyer group ID; the auction service treats this as a First Dibs buy-now. */
      buyerGroupId: string
    }

const buyNow = async (
  { auctionId, amount, buyerGroupId }: BuyNowParams,
  accessToken: string
): Promise<PlaceBidResponse> => {
  const bidsClient = getBidsClient()
  const axiosConfig = buildAxiosRequestConfig({ accessToken })

  // For First Dibs buy-now: pass amount (earlyPrice) and buyerGroupId in the request body.
  // The generated client doesn't model a request body for buyNow, so we inject it via
  // axiosConfig.data — axios uses that as the POST body.
  const response = await bidsClient.buyNow(
    { id: auctionId },
    buyerGroupId
      ? { ...axiosConfig, data: { amount, buyerGroupId } }
      : axiosConfig
  )

  return { ...response.data }
}

const useBuyNowMutation = () => {
  const accessToken = useAccessToken()

  return useMutation({
    // This mutation should only ever be used by authenticated users, so accessToken should never be undefined here...
    // but the mutation gets set up for both authenticated and unauthenticated users so we can't (easily) use
    // `useAccessToken({ required: true })` here and guarantee it will be defined for Typescript
    mutationFn: (params: BuyNowParams) => buyNow(params, accessToken || ''),
  })
}

export default useBuyNowMutation
